import { cloneDeep, escape } from 'lodash'
import he from 'he'
import DateUtils from '@/utils/date'
import { handleStyleConfig } from '@/utils/generate-html/layout'

/**
 * 生成一个由td标签包裹的字符串
 * @param obj {object} {width, rowspan, colspan, content}
 * @return {`<td>string</td>`}
 */
function tableTdTemplate(obj) {
  let content = ''
  if (Array.isArray(obj.content)) {
    obj.content.forEach(item => {
      if (item?.html) {
        content += item.html
      } else if (item?.content) {
        content += `<div style="margin-bottom: 8px; word-wrap: break-word; word-break: normal; white-space: break-spaces; ${
          item.style
        }">${he.encode((item.content || '').toString())}</div>`
      } else {
        content += `<div style="margin-bottom: 8px; word-wrap: break-word; word-break: normal; white-space: break-spaces; ">${he.encode(
          (item || '').toString()
        )}</div>`
      }
    })
  } else if (obj.html) {
    content = obj.html
  } else {
    content = he.encode((obj.content || '').toString())
  }

  return `<td style="width: ${obj.width || 'auto'}; text-align: ${obj.align ||
    'left'}; word-wrap: break-word; word-break: normal; white-space: break-spaces;  ${obj.style ||
    ''}" colspan="${obj?.colspan || 1}" rowspan="${obj?.rowspan ||
    1}">${content}</td>`
}

/**
 * 生成一个字符串 包含一个或多个由tr标签包裹的内容
 * @param list {Array}
 * @return {string}
 */
export function tableTrTemplate(list) {
  const trPackage = content => `<tr>${content}</tr>`
  const loop = list => {
    return list
      .map(value => {
        if (Array.isArray(value)) {
          return trPackage(loop(value))
        } else {
          return tableTdTemplate(value)
        }
      })
      .join('')
  }
  let trList = loop(list)
  if (trList.indexOf('tr') < 0) {
    trList = trPackage(trList)
  }

  return trList
}

/**
 * 生成pdf的头部信息
 */
function pdfHeadInfoTemplate(describeInfo = []) {
  const loop = list => {
    const contentString = list.map(value => {
      if (value?.html) {
        return `<div class="project-item" style="text-align: ${value.align ||
          'left'}; ${value.style || ''}">${value.label || ''}${
          value.label ? '：' : ''
        }<div>${value.html || ''}</div></div> `
      }

      return `<span class="project-item" style="text-align: ${value.align ||
        'left'}; ${value.style || ''}">${value.label || ''}${
        value.label ? '：' : ''
      }${he.encode((value.content || '').toString())}</span> `
    })

    return `
     <div class="project">
       ${contentString.join('')}
     </div>
    `
  }

  if (!Array.isArray(describeInfo)) return ''

  return describeInfo
    .map(headInfo => {
      return loop(headInfo)
    })
    .join('')
}

/**
 * 内容生成
 * @params tableData {Object} 表格对象，包含表头和表格体的描述
 * @params describeInfoTop {Array} 顶部描述信息数组
 * @params describeInfoBottom {Array} 底部描述信息数组
 * @params tableStyleConfig {Object} 表格样式配置
 */
function contentWrapper(
  tableData,
  describeInfoTop,
  describeInfoBottom,
  tableStyleConfig
) {
  const tableHead = tableTrTemplate(tableData.head)
  const tableBody = tableTrTemplate(tableData.body)
  const headInfo = pdfHeadInfoTemplate(describeInfoTop)
  const bottomInfo = pdfHeadInfoTemplate(describeInfoBottom)

  const tableStyleStr = handleStyleConfig(tableStyleConfig.tableStyle)
  const tbodyStyleStr = handleStyleConfig(tableStyleConfig.tbodyStyle)

  return `
  <div class="content">
      ${headInfo}

      <table class="table" border="1" cellspacing="0" cellpadding="10" style="table-layout: fixed; word-break:break-all;  ${tableStyleStr}">
        <tbody style="${tbodyStyleStr}">
        ${tableHead}
        ${tableBody}
        </tbody>
      </table>

      ${
  bottomInfo
    ? '<div style="margin-top: 16px">' + bottomInfo + '</div>'
    : ''
}
    </div>
  `
}

/**
 * 用page标签包裹表格html字符串
 * @params pdfTitle {String} 单页标题
 * @params contentString {String} 内容html模板
 */
export function pageWrapper(pdfTitle, contentString) {
  return `
  <div class="page">
      <h1 style="text-align: center">${escape(pdfTitle)}</h1>
      ${contentString}
    </div>
  `
}

/**
 * 用html包裹表格body字符串
 * @param {String} bodyHtmlString  body的html字符串
 * @param {String} pdfTitle  网页标题
 * @param {horizontal|vertical} dircetion 默认horizontal
 * @param {Object} pageSettingObj 页面页脚设置({ key: content }) key为页面位置，content为内容；如{ top-left: '页面左上角内容' }
 * @param {String} bodyFontSize body的字体大小，默认10px
 */
export function tableHtmlWrapper(
  bodyHtmlString,
  pdfTitle,
  direction = 'horizontal',
  pageSettingObj = {},
  bodyFontSize = '10px'
) {
  const pageSettingStr = Object.keys(pageSettingObj)
    .map(key => {
      return `@${key}{
      content: ${pageSettingObj[key]};
      font-family: SimSun;
      font-size: 12px;
      color: #000;
    }`
    })
    .join(' ')

  return `
<!DOCTYPE html>
<html lang="zh_CN">
  <head>
    <meta charset="UTF-8" />
    <meta http-equiv="X-UA-Compatible" content="IE=edge" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>${he.encode(pdfTitle || '')}</title>

    <style>
      @page {
        ${direction === 'horizontal' ? 'size: A4 landscape;' : 'size: A4;'}

        @bottom-center {
          content: "第" counter(page) "页/共 " counter(pages) "页";
          font-family: SimSun;
          font-size: 12px;
          color: #000;
        }
        ${pageSettingStr}
      }

      body {
        margin: 0;
        padding: 0;
        font-family: SimSun !important;
        font-size: ${bodyFontSize};
      }

      span {
        font-family: SimSun !important;
      }

      .m-t-16 {
        margin-top: 16px;
      }

      .m-t-24 {
        margin-top: 24px;
      }

      .page {
        text-align: center;
        page-break-after: always;
      }

      .page:last-child {
        page-break-after: auto;
      }

      .content {
        margin-bottom: 16px;
      }

      .content:last-child {
        margin-bottom: 0;
      }

      .project {
        width: 100%;
        display: table;
        margin-bottom: 8px;
      }

      .project .project-item {
        text-align: left;
        display: table-cell;
      }

      .project::after {
        content: "";
        display: inline-block;
        height: 0;
        width: 100%;
      }

      .table {
        width: 100%;
      }

      table tbody tr td {
        width: 100px;
      }

      th,tr,td,tfoot {
        page-break-inside: avoid;
      }
    </style>
  </head>
  <body>
    ${bodyHtmlString}
  </body>
</html>
`
}

/**
 * 对传入的表格数据进行处理
 */
export function generateTableData(tableHead, tableData, tableConfig) {
  if (!tableHead) {
    return [
      {
        head: [
          ...(tableHead?.fixedPrev || []),
          ...(tableHead?.dynamic || []),
          ...(tableHead?.fixedLast || [])
        ],
        body: tableData
      }
    ]
  }

  const dynamicNum = tableHead?.dynamicNum || tableHead.dynamic?.length || 0
  if (!dynamicNum && tableHead.dynamic?.length) {
    console.error('当dynamic数组不为空时，dynamicNum不能为零')
    return []
  }

  const fixedPrevLength = getMaxColOrRowNum(
    tableHead.fixedPrev || [],
    'colspan'
  )
  const fixedLastLength = getMaxColOrRowNum(
    tableHead.fixedLast || [],
    'colspan'
  )
  const headGroup = splitTableToGroup(
    tableHead.fixedPrev || [],
    tableHead.dynamic || [],
    dynamicNum,
    tableHead.fixedLast || []
  )

  const tempTableData = cloneDeep(tableData)
  const dataGroup = tempTableData.map(item => {
    const fixedPrevData = []
    const fixedLastData = []
    if (fixedPrevLength) {
      fixedPrevData.push(spliceListByColspan(item, fixedPrevLength))
    }
    if (fixedLastLength) {
      fixedLastData.push(spliceListByColspan(item, fixedLastLength, true))
    }
    return splitTableToGroup(
      fixedPrevData,
      item,
      dynamicNum,
      fixedLastData
    ).flat()
  })
  // console.log('dataGroup', JSON.stringify(dataGroup))

  // 获取根据表头分割后表格列表
  const tableListByHead = headGroup.map((head, headIndex) => {
    return {
      head: head,
      body: dataGroup.map((item, index) => {
        const arr = []
        if (item[headIndex]?.length > 0) {
          arr.push(...item[headIndex])
        }
        return arr
      })
    }
  })

  if (tableConfig?.needTableSplit) {
    // 在表头分割后的表格列表下，获取根据内容分割后的表格列表
    return splitPageListByColumnContent(
      tableListByHead,
      tableConfig?.contentMaxLength || 360
    )
  } else {
    return tableListByHead
  }
}

/**
 * 根据colspan属性分割数组，返回切分结果，会修改原数组
 * @param {Array} list 原数组
 * @param {Number} spliceCount 切割长度
 * @param {Boolean} reverse 是否从尾部开始切割
 * @returns {*[]} 切割结果数组
 */
function spliceListByColspan(list, spliceCount, reverse = false) {
  const result = []
  // 定义累计长度
  let colspanLength = 0
  if (reverse) {
    // 逆序从右到左截取
    for (let i = list.length - 1; i >= 0; i--) {
      colspanLength = colspanLength + (list[i]?.colspan || 1)
      if (colspanLength <= spliceCount) {
        result.push(list[i])
      } else {
        break
      }
    }
    list.splice(-1, result.length)
  } else {
    // 顺序从左到右截取
    for (let i = 0; i < list.length; i++) {
      colspanLength = colspanLength + (list[i]?.colspan || 1)
      if (colspanLength <= spliceCount) {
        result.push(list[i])
      } else {
        break
      }
    }
    list.splice(0, result.length)
  }
  return result
}

function splitTableToGroup(fixedPrev, dynamic, dynamicNum, fixedLast) {
  const dynamicIs2d = isDyadic(dynamic)
  const dynamicTemp = handleDynamicHeader(dynamic, dynamicIs2d, dynamicNum)

  const fixedPrevIs2d = isDyadic(fixedPrev)
  let fixedPrevTemp = cloneDeep(fixedPrev)
  if (!fixedPrevIs2d) {
    fixedPrevTemp = [fixedPrevTemp]
  }

  const fixedLastIs2d = isDyadic(fixedLast)
  let fixedLastTemp = cloneDeep(fixedLast)
  if (!fixedLastIs2d) {
    fixedLastTemp = [fixedLastTemp]
  }

  if (
    !dynamic.length ||
    (dynamicTemp.length === 1 && !dynamicTemp[0]?.length)
  ) {
    const length = Math.max(fixedPrevTemp.length, fixedLastTemp.length)
    const temp = []
    for (let i = 0; i < length; i++) {
      temp.push([...(fixedPrevTemp[i] || []), ...(fixedLastTemp[i] || [])])
    }
    return [[...temp]]
  }

  return dynamicTemp.map(tableHeader => {
    return tableHeader.map((value, index) => {
      return [
        ...(fixedPrevTemp[index] || []),
        ...value,
        ...(fixedLastTemp[index] || [])
      ]
    })
  })
}

/**
 * 处理动态表格头，并返回处理后的数据
 * @param {Array} dynamic 动态表格头数据
 */
function handleDynamicHeader(dynamic, is2d, dynamicNum) {
  if (!dynamic?.length) return []
  let tempList = cloneDeep(dynamic)
  if (!is2d) {
    tempList = [tempList]
  }
  const maxColNum = getMaxColOrRowNum(dynamic, 'colspan')
  // TODO: 这里可以做一个额外的判断，如果colNumList里面的数据不是完全相等，则抛出一个错误
  // if (false) {
  //   error("表格生成错误：传入的表格数据异常-动态表头列数不对等");
  // }

  const resultList = []
  const loopTime = Math.ceil(maxColNum / dynamicNum)
  for (let i = 0; i < loopTime; i++) resultList.push([])

  tempList.forEach(parentArray => {
    parentArray = cloneDeep(parentArray)
    let time = 0

    while (time < loopTime) {
      let dynamicNumTemp = dynamicNum
      const temp = []

      parentArray.forEach(value => {
        const col = 'colspan' in value ? Number(value?.colspan) : 1
        let recordCol = 0
        if (col === 0 || dynamicNumTemp === 0) return

        if (dynamicNumTemp >= col) {
          value.colspan = 0
          recordCol = col
          dynamicNumTemp = dynamicNumTemp - col
        } else {
          value.colspan = col - dynamicNumTemp
          recordCol = dynamicNumTemp
          dynamicNumTemp = 0
        }

        const tempItem = Object.assign({}, value, { colspan: recordCol })
        temp.push(tempItem)
      })

      resultList[time].push(temp)
      time++
    }
  })

  return resultList
}

/**
 * 判断数组是否是二维
 * @param list
 * @returns {boolean}
 */
function isDyadic(list) {
  if (list?.length === 0 || !list) return false

  let weidu = 0
  if (Array.isArray(list)) {
    weidu++
  }
  const result = list.every(value => {
    if (Array.isArray(value)) {
      return true
    }
    return false
  })
  if (result) weidu++
  return weidu === 2
}

/**
 * 获取最大的行数或列数
 * @property {Array} list 列表
 * @property {'colspan'|'rowspan'} key 字段
 */
function getMaxColOrRowNum(list, key) {
  const is2d = isDyadic(list)
  let tempList = cloneDeep(list)
  const numList = []
  if (!is2d) {
    tempList = [tempList]
  }

  tempList.forEach(parentArray => {
    let colNum = 0
    parentArray.forEach(value => {
      colNum = colNum + (value[key] || 1)
    })
    numList.push(colNum)
  })
  return Math.max(...numList)
}

function splitPageListByColumnContent(pageList, splitCount) {
  return pageList
    .map(page => {
      const tableBodyList = splitTablebody(page.body || [], splitCount)
      const resultPage = []
      if (!tableBodyList.length) return page

      tableBodyList.forEach(body => {
        resultPage.push({
          head: page.head,
          body
        })
      })
      return resultPage
    })
    .flat()
}

function splitTablebody(body, splitCount) {
  let needLoop = true
  const tableBodyList = []
  let bodyCopy = cloneDeep(body)

  if (!bodyCopy.length) needLoop = false

  while (needLoop) {
    const colWordCountList = []
    const colSumWordCountList = []
    const bodyTemp = []

    for (let row = 0; row < bodyCopy.length; row++) {
      if (row === bodyCopy.length - 1) needLoop = false

      const rowValue = bodyCopy[row] || []
      rowValue.forEach((value, col) => {
        colWordCountList[col] = value?.content?.length || 0
        colSumWordCountList[col] =
          (colSumWordCountList[col] || 0) + colWordCountList[col]
      })
      const currentMaxColWord = Math.max(...colWordCountList)
      const currentMaxColSumWord = Math.max(...colSumWordCountList)

      if (currentMaxColWord >= splitCount) {
        bodyTemp.length && tableBodyList.push(bodyTemp)
        tableBodyList.push([rowValue])
        bodyCopy = bodyCopy.slice(row + 1)
        break
      }

      bodyTemp.push(rowValue)
      if (currentMaxColSumWord >= splitCount) {
        tableBodyList.push(bodyTemp)
        bodyCopy = bodyCopy.slice(row + 1)
        break
      }

      if (row === bodyCopy.length - 1) {
        tableBodyList.push(bodyTemp)
      }
    }
  }

  return tableBodyList
}

/**
 *
 * @param {String} pdfTitle  pdf的标题
 * @param {Array} describeInfoTop  [[{label, content, align}], [{}, {}]] 二维数组，一维表示行，二维表示该行展示的数据
 * @param {Array} describeInfoBottom  [[{label, content, align}], [{}, {}]] 二维数组，一维表示行，二维表示该行展示的数据
 * @param {Object} tableHead  {fixedPrev: [{content, width, style, field: 没有dynamicNum时可不传}], fixedLast: [], dynamic: [], dynamicNum: Number } 支持一维和多维数组，合并行时使用多维
 * @param {Object[]} tableData  [{content, width, style, field}]
 * @param {String} timeInfo  目前功能没有
 * @param {Boolean} needHtmlWrapper  是否需要使用html包裹
 * @param {Object} tableConfig 表格配置项，目前支持needTableSplit(是否需要将表格拆页，为true后面的参数才会起作用)、contentMaxLength(进行页面分割的最大内容长度，超出则要强制分页)
 * @param {Object} htmlConfig html容器配置，目前支持bodyFontSize
 * @returns {string}
 */
export function generateTableHtml({
  pdfTitle,
  describeInfoTop,
  describeInfoBottom,
  tableHead,
  tableData,
  timeInfo,
  needHtmlWrapper = true,
  needPageWrapper = true,
  direction = 'horizontal',
  tableConfig,
  htmlConfig = { bodyFontSize: '10px' }
}) {
  const tableList = generateTableData(tableHead, tableData, tableConfig)
  const contentList = (tableList || []).map(table =>
    contentWrapper(
      table,
      describeInfoTop,
      describeInfoBottom,
      tableConfig?.styleConfig || {}
    )
  )
  if (needPageWrapper) {
    const pageString = contentList
      .map(contentString => pageWrapper(pdfTitle, contentString))
      .join(' ')
    if (needHtmlWrapper) {
      return tableHtmlWrapper(
        pageString,
        pdfTitle,
        direction,
        {},
        htmlConfig.bodyFontSize
      )
    }
    return pageString
  }

  return contentList.join(' ')
}

/**
 * 将element-ui里定义的table表头格式转换成此html需要的格式
 * @param {*} header
 * @returns
 */
export function translateTableHeader(header) {
  const headerColumn = header
    .filter(item => !item.hide)
    .map(el => {
      return {
        content: el.label,
        style: `text-align: ${el?.columnAttrs?.align || 'center'}`,
        width: el.width || el.minWidth || '80px'
      }
    })
  headerColumn.unshift({
    content: '序号',
    style: 'text-align: center',
    width: '50px'
  })
  return {
    dynamic: headerColumn
  }
}

/**
 * 将element-ui里定义的table数据转换成此html需要的格式
 * @param {*} list
 * @param {*} header
 */
export function translateTableData(list, header) {
  return list.map((el, index) => {
    const arr = header
      .filter(item => !item.hide)
      .map(obj => {
        let content = ''
        if (obj.filter) {
          content = obj.filter(obj.prop ? el[obj.prop] : el)
        } else if (obj.tagDict) {
          content = obj.tagDict.translate(el[obj.prop]).label || '-'
        } else if (obj.isNormMinute) {
          content = DateUtils.getNormMinute(el[obj.prop])
        } else if (obj.isNormDay) {
          content = DateUtils.getNormDay(el[obj.prop])
        } else {
          content = el[obj.prop]
        }
        return {
          content,
          style: `text-align: ${obj?.columnAttrs?.align || 'center'}`
        }
      })
    arr.unshift({
      content: Number(index) + 1,
      style: 'text-align: center'
    })
    return arr.filter(el => !el.hide)
  })
}
